import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router';
import { Spinner } from '@vwt/ui';
import { AppShell } from '../components/shell.js';
import { useSession } from '../lib/session.js';
import { AuthErrorPage, KeyRedirect, NotFoundPage, RouteErrorPage, ServiceErrorPage, SignedOutPage, SignInRedirect } from '../features/auth/auth-pages.js';
import { MyWorkPage } from '../features/my-work/my-work-page.js';
import { WorkListPage } from '../features/work/work-list-page.js';
import { WorkDetail } from '../features/work/work-detail.js';
import { RequestsPage } from '../features/requests/requests-page.js';
import { RequestDetail } from '../features/requests/request-detail.js';
import { IntakePage } from '../features/intake/intake-page.js';
import { ProjectPage, ProjectsPage } from '../features/projects/projects-pages.js';
import { TeamPage, TeamsPage } from '../features/teams/teams-pages.js';
import { SettingsPage } from '../features/settings/settings-page.js';
import { SlackLinkPage } from '../features/settings/slack-link-page.js';
import { InboxPage } from '../features/inbox/inbox-page.js';
import { TutorialPage } from '../features/tutorial/tutorial-page.js';

// Heavier, less frequent screens load on demand.
const AdminPage = lazy(() => import('../features/admin/admin-page.js').then((m) => ({ default: m.AdminPage })));
const DashboardsPage = lazy(() => import('../features/dashboards/dashboards-page.js').then((m) => ({ default: m.DashboardsPage })));
const AutomationsPage = lazy(() => import('../features/automations/automations-page.js').then((m) => ({ default: m.AutomationsPage })));
const ReportsPage = lazy(() => import('../features/reports/reports-page.js').then((m) => ({ default: m.ReportsPage })));
// The document editor (Tiptap) loads only when someone opens documents.
const DocumentsLayout = lazy(() => import('../features/documents/documents-pages.js').then((m) => ({ default: m.DocumentsLayout })));
const DocumentsHome = lazy(() => import('../features/documents/documents-pages.js').then((m) => ({ default: m.DocumentsHome })));
const FolderPage = lazy(() => import('../features/documents/documents-pages.js').then((m) => ({ default: m.FolderPage })));
const DocumentPage = lazy(() => import('../features/documents/document-page.js').then((m) => ({ default: m.DocumentPage })));
const ArchivePage = lazy(() => import('../features/documents/documents-pages.js').then((m) => ({ default: m.ArchivePage })));
const DocumentsTablePage = lazy(() => import('../features/documents/documents-table.js').then((m) => ({ default: m.DocumentsTablePage })));
const DatabasePage = lazy(() => import('../features/documents/databases/database-page.js').then((m) => ({ default: m.DatabasePage })));
// The anonymous page of a public document (ADR-0056): outside the signed-in shell, no editor bundle.
const PublicDocumentPage = lazy(() => import('../features/public/public-document-page.js').then((m) => ({ default: m.PublicDocumentPage })));

function Loading() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner />
    </div>
  );
}

/** Requires an authenticated session; anonymous visitors go through corporate sign-in. */
function Protected() {
  const { status } = useSession();
  if (status === 'loading') return <Loading />;
  if (status === 'anonymous') return <SignInRedirect />;
  if (status === 'error') return <ServiceErrorPage />;
  return <AppShell />;
}

function WorkPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <div className="mx-auto h-full max-w-6xl bg-surface md:border-x md:border-border">
      <WorkDetail id={id!} />
    </div>
  );
}

function RequestPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <div className="mx-auto h-full max-w-6xl bg-surface md:border-x md:border-border">
      <RequestDetail id={id!} />
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/auth-error', element: <AuthErrorPage />, errorElement: <RouteErrorPage /> },
  { path: '/signed-out', element: <SignedOutPage /> },
  {
    path: '/p/:token',
    element: (
      <Suspense fallback={<Loading />}>
        <PublicDocumentPage />
      </Suspense>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    element: <Protected />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to="/my-work" replace /> },
      { path: '/my-work', element: <MyWorkPage /> },
      { path: '/requests', element: <RequestsPage /> },
      { path: '/requests/new', element: <IntakePage /> },
      { path: '/requests/:id', element: <RequestPage /> },
      { path: '/work', element: <WorkListPage /> },
      { path: '/work/:id', element: <WorkPage /> },
      { path: '/projects', element: <ProjectsPage /> },
      { path: '/projects/:id', element: <ProjectPage /> },
      { path: '/teams', element: <TeamsPage /> },
      { path: '/teams/:id', element: <TeamPage /> },
      {
        path: '/docs',
        element: (
          <Suspense fallback={<Loading />}>
            <DocumentsLayout />
          </Suspense>
        ),
        children: [
          { index: true, element: <Suspense fallback={<Loading />}><DocumentsHome /></Suspense> },
          { path: 'folders/:id', element: <Suspense fallback={<Loading />}><FolderPage /></Suspense> },
          { path: 'archive', element: <Suspense fallback={<Loading />}><ArchivePage /></Suspense> },
          { path: 'table', element: <Suspense fallback={<Loading />}><DocumentsTablePage /></Suspense> },
          { path: 'databases/:id', element: <Suspense fallback={<Loading />}><DatabasePage /></Suspense> },
          { path: ':id', element: <Suspense fallback={<Loading />}><DocumentPage /></Suspense> },
        ],
      },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/inbox', element: <InboxPage /> },
      { path: '/slack/link', element: <SlackLinkPage /> },
      { path: '/tutorial', element: <TutorialPage /> },
      {
        element: (
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          { path: '/reports', element: <ReportsPage /> },
          { path: '/dashboards', element: <DashboardsPage /> },
          { path: '/dashboards/:id', element: <DashboardsPage /> },
          { path: '/automations', element: <AutomationsPage /> },
          { path: '/admin', element: <AdminPage /> },
        ],
      },
      { path: '/go/:key', element: <KeyRedirect /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
